import type { Point } from "../scene/model";

/** Column-major affine matrix: x' = ax + cy + e, y' = bx + dy + f. */
export type AffineMatrix = readonly [
  number,
  number,
  number,
  number,
  number,
  number,
];

export function readAffineMatrix(
  value: readonly number[],
): AffineMatrix | null {
  return value.length === 6 && value.every(Number.isFinite)
    ? ([...value] as unknown as AffineMatrix)
    : null;
}

export function transformPoint(point: Point, matrix: AffineMatrix): Point {
  return {
    x: matrix[0] * point.x + matrix[2] * point.y + matrix[4],
    y: matrix[1] * point.x + matrix[3] * point.y + matrix[5],
  };
}

export function invertAffine(matrix: AffineMatrix): AffineMatrix | null {
  const determinant = matrix[0] * matrix[3] - matrix[1] * matrix[2];
  if (!Number.isFinite(determinant) || determinant === 0) return null;
  const inverse = 1 / determinant;
  const linear = [
    inverse * matrix[3],
    -inverse * matrix[1],
    -inverse * matrix[2],
    inverse * matrix[0],
  ];
  return readAffineMatrix([
    ...linear,
    -(linear[0]! * matrix[4] + linear[2]! * matrix[5]),
    -(linear[1]! * matrix[4] + linear[3]! * matrix[5]),
  ]);
}
